import React from 'react';
import { generateId, shortHandDateTime} from '../../utility/functions';
import { QuestRoute } from '../../api/QuestRoutes';
import Table, { Ctd } from '../../table/Table';
import { IsAuthorized, userRoles } from '../../RoleProtected';
import Page from '../../layout/Page';
import Col from '../../layout/Col';
import Image from '../../Image';
import Button from '../../forms/Button';
import ModalFactory from '../../Modal';
import LinkTo from '../../forms/LinkTo';
import ManagementRow from '../../table/ManagementRow';
import Row from '../../layout/Row';
import Flag from './Flag';
import AssignPlateReadAwardForm from '../../processors/AssignPlateReadAwardForm';
import AddPlateReadMessageForm from '../../processors/AddPlateReadMessageForm';
import RequireFeature, { Features } from '../../RequireFeature';
import usePlateReadActions from './usePlateReadActions';

export const emptyGuid = '00000000-0000-0000-0000-000000000000';

export const hasCustomer = (e) => !!e.customerId && e.customerId !== emptyGuid;

// Returns the LinkTo props needed to reach the account tied to a plate read,
// or null if the plate isn't associated with an account (e.g. a Visitor).
export const getAccountLinkProps = (e) => {
    if (hasCustomer(e) && e.loyaltyType === 'AppWashClub')
        return { customerId: e.customerId };
    if (hasCustomer(e) && e.loyaltyAccountId !== undefined)
        return { loyaltyAccountId: e.loyaltyAccountId };
    return null;
}

export const renderCustomerLink = (e, canViewCustomers) => {
        const accountLink = getAccountLinkProps(e);

        if (accountLink)
            return canViewCustomers ?
                <LinkTo target='_blank' {...accountLink}>{e.customerName}</LinkTo>
                : <div>{e.customerName}</div>

        return canViewCustomers ?
            <LinkTo target='_blank' to={'/visitor/' + e.plate}>{e.customerName} {e.plate}</LinkTo>
            : <div>{e.customerName}{e.plate}</div>
    }

const LicensePlateReads = ({ ...props }) => {
    const canViewCustomers = IsAuthorized(userRoles.viewCustomers);
    const canViewTransactions =  IsAuthorized(userRoles.viewAuditReports);

    const { renderActionButtons } = usePlateReadActions(() => window.DateRange());

    const tableConfig = {
        tableId: 'plateReads',
        endpoint: QuestRoute.plateReads.list,
        isReport: true,
        controls: [
            <Button 
                onClick={() =>{
                    const id = generateId();
                    const platesList = [...new Set(window.plateReadsTableData.map(read => read.plate))]; 
                    ModalFactory.show(window.plateReadsTableData.length ? {
                        id,
                        title: 'Assign Plate Read Award',
                        body: <AssignPlateReadAwardForm modalId={id} platesList={platesList} />
                    } : {
                        id,
                        title: 'No Users',
                        body: 'No users selected, please have at least one user.',
                        type: 'error'
                    })
                }}
                label='Plate Award'
            />,
            <Button
                onClick={() => {
                    const id = generateId();
                    const platesList = [...new Set(window.plateReadsTableData.map(read => read.plate))]; 
                    ModalFactory.show(window.plateReadsTableData.length ? {
                        id,
                        title: 'Add Plate Read Message',
                        body: <AddPlateReadMessageForm modalId={id} platesList={platesList} />
                    } : {
                        id,
                        title: 'No Users',
                        body: 'No users selected, please have at least one user.',
                        type: 'error'
                    })                    
                }}
                label='Plate Message'
            />
        ],  
        headers: [
            {
                label: 'Date',
                propKey: 'dateRead',
                type: 'date'
            }, {
                label: 'Plate',
                propKey: 'plate',
            }, {
                label: 'Plate Images',
                propKey: 'plate'
            }, {
                label: 'Customer',
                propKey: 'customerName'
            }, {
                label: 'Location',
                propKey: 'location',
                options: true,
            }, {
                label: 'Record',
                propKey: 'transRecordId',
            }, {
                label: 'Transaction Type',
                propKey: 'transactionType'
            },  {
                label: 'Alerts',
                propKey: 'flags'
            }, {
                label: 'Message',
                propKey: 'kioskMessage',
                show: false
            }
        ],
        quickSort: [
            {
                label: 'Customers',
                propKey: 'customerName',
                query: /^(?!Visitor$).+/,
                color: '#ff9909ff'
            },
            {
                label: 'Visitors',
                propKey: 'customerName',
                query: /^Visitor$/,
                color: '#ff9909ff'
            },
            {
                label: 'Alerts',
                propKey: 'flags',
                query: /.+/i,
                color: '#93090e',
            },
        ],       
        renderFormat: (e) => (
            <ManagementRow 
                key={generateId()}
                controls={renderActionButtons(e)} 
                layout='elipsis' 
            >             
                <td>{shortHandDateTime(e.dateRead)}</td>
                <td>
                    {e.plate}
                </td>
               
                <Ctd>
                    <Col>
                        <Image
                            src={'data:image/gif;base64,' + e.smallPlateImage}
                            modalTitle={e.plate}
                            alt='Car'
                        />
                        <Button
                            label='Full Image'
                            onClick={() => ModalFactory.show({ title: props.plate, body: <Image src={e.fullPlateImageUrl} /> })}
                        />
                    </Col>
                </Ctd>
                <td>
                    {renderCustomerLink(e, canViewCustomers)}
                </td>
                <td>{e.location}</td>
                <td>
                    {canViewTransactions ?
                        <LinkTo
                            target='_blank'
                            to={'/reporting/sales/transaction/' + e.transRecordId}>{e.transRecordId}</LinkTo> : <div>{e.transRecordId}</div>
                    }
                </td>
                <td>
                    {e.transactionType}
                </td>
                <td>
                    <Row>
                    {e.flags.map((flag) => (
                            <Flag key={generateId()} flag={flag} />
                        ))
                    }
                    </Row>
                </td>
                <td>
                    {e.kioskMessage}
                </td>
            </ManagementRow>
        )
    };

    return (
        <RequireFeature requiredFeatures={Features.PlateReads}>
            <Page title='License Plate Reads' icon='camera'>
                <Table config={tableConfig} />
            </Page>
        </RequireFeature>
    );
}

export default LicensePlateReads;
